import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Gift } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MUTE_KEY = 'notif_sound_muted';

/**
 * Two-tone coin chime — plays when the rebate chip pulses. Uses WebAudio
 * so we don't ship an mp3. Mario-esque ascending E5 → C6 through a
 * triangle wave, low gain, ~320 ms total so it never overwhelms whatever
 * game sound is already playing.
 *
 * Respects the shared `notif_sound_muted` localStorage flag so muting the
 * bell also silences this chime — one mute for all "you got something!"
 * alerts.
 */
let _rebateAudioCtx = null;
const playCoinChime = () => {
    try {
        if (typeof window === 'undefined') return;
        if (localStorage.getItem(MUTE_KEY) === '1') return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!_rebateAudioCtx) _rebateAudioCtx = new Ctx();
        const ctx = _rebateAudioCtx;
        if (ctx.state === 'suspended') ctx.resume();
        const tone = (freq, when, dur = 0.14, gain = 0.11) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = 'triangle';
            osc.frequency.value = freq;
            g.gain.setValueAtTime(0.0001, ctx.currentTime + when);
            g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + when + 0.015);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + dur);
            osc.connect(g); g.connect(ctx.destination);
            osc.start(ctx.currentTime + when);
            osc.stop(ctx.currentTime + when + dur + 0.05);
        };
        // E5 → C6 (classic pickup-coin arpeggio)
        tone(659.25, 0);
        tone(1046.5, 0.09);
    } catch { /* ignore autoplay-policy or unsupported browsers */ }
};

/**
 * RebateCountdownChip — tiny cyan/gold chip that appears in the header
 * whenever the player has any pending 6% cashout-fee rebate accrued this
 * week. It closes the loop on the fee narrative: right there in the top
 * bar, "🎁 $3 coming Mon" reminds the player they're getting the fee
 * back — no need to hunt for it in Profile.
 *
 * Live-grow animation (Aug 2026): when the pending total ticks UP between
 * polls (i.e. the player just cashed out and a fresh 6% fee got added),
 * the chip flashes gold + scales briefly AND plays a soft coin chime so
 * players SEE and HEAR their rebate pool grow in real time.
 *
 * Hidden for admin/cashier accounts, hidden at $0, silently no-ops if
 * the endpoint 4xx's. Refreshes every 90s.
 */
const POLL_MS = 90_000;

const RebateCountdownChip = ({ variant = 'desktop' }) => {
    const { user } = useAuth();
    const [pending, setPending] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [pulse, setPulse] = useState(false);
    const prevPendingRef = useRef(null);

    useEffect(() => {
        if (!user || user.role !== 'user') return;
        let cancelled = false;
        const fetchPreview = async () => {
            try {
                const { data } = await axios.get(
                    `${API}/user/cashout-rebate-preview`,
                    { withCredentials: true },
                );
                if (cancelled) return;
                const next = Number(data?.pending_rebate) || 0;
                const prev = prevPendingRef.current;
                if (prev != null && next > prev + 0.001) {
                    setPulse(true);
                    playCoinChime();
                    setTimeout(() => setPulse(false), 1800);
                }
                prevPendingRef.current = next;
                setPending(next);
                setLoaded(true);
            } catch {
                if (!cancelled) setLoaded(true);
            }
        };
        fetchPreview();
        const id = setInterval(fetchPreview, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, [user]);

    if (!user || user.role !== 'user') return null;
    if (!loaded || pending <= 0) return null;

    const base = variant === 'mobile'
        ? 'inline-flex items-center gap-1 px-2 py-1 text-[10px]'
        : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px]';

    // Pulse turns the chip from cyan → gold + adds a soft ping ring for
    // ~1.8s so a growth event is unmissable in the header noise.
    const tone = pulse
        ? 'border-[#FFD700] bg-[#FFD700]/20 text-[#FFD700] shadow-[0_0_16px_rgba(255,215,0,0.55)] scale-105'
        : 'border-[#00FFFF]/40 bg-[#00FFFF]/10 text-[#00FFFF]';

    return (
        <span
            className={`${base} ${tone} relative rounded-full font-bold border whitespace-nowrap transition-all duration-500 ease-out`}
            data-testid="header-rebate-chip"
            data-pulsing={pulse ? '1' : '0'}
            title={`$${pending.toFixed(2)} in 6% cashout fees will rebate as Free Play (1× rollover) this coming Monday at 9 AM CT.`}
        >
            {pulse && (
                <span
                    className="absolute inset-0 rounded-full animate-ping bg-[#FFD700]/30 pointer-events-none"
                    aria-hidden="true"
                />
            )}
            <Gift className={variant === 'mobile' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            <span className="tabular-nums">${pending.toFixed(2)}</span>
            <span className={`font-normal ${pulse ? 'text-[#FFD700]/80' : 'text-[#A0AAB2]'}`}>
                coming Mon
            </span>
        </span>
    );
};

export default RebateCountdownChip;
